/**
 * Perspective warp of a webcam frame into the CNN's virtual camera, on the GPU.
 *
 * The reference (eye-tracking-sandbox v0.1.0, vision/normalization.py) does
 * `cv2.warpPerspective(frame, W, (448, 448))` — bilinear, BORDER_CONSTANT black —
 * and hands the 448² uint8 crop to the ONNX graph, which does its own
 * antialiased resize to 224² and the ImageNet normalisation. So this warper
 * stops at 448² uint8: everything after that happens inside the same model file
 * the gaze container runs, not in a reimplementation of it.
 *
 * Canvas 2D cannot do this — its transform is affine and a homography is not —
 * hence one WebGL2 pass.
 */
import { inverse3, type Mat3 } from './linalg'

export const CROP_SIZE = 448

const VERTEX_SRC = `#version 300 es
in vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }`

/**
 * Inverse-maps each destination pixel through W⁻¹ and samples the source.
 *
 * `uWinv` is the INVERSE homography: W is the forward source→destination map
 * and a fragment shader runs per destination pixel. Getting this backwards
 * yields a crop that is inside-out but still looks like a face.
 */
const WARP_SRC = `#version 300 es
precision highp float;
uniform sampler2D uSrc;
uniform mat3 uWinv;
uniform vec2 uSrcSize;
out vec4 fragColor;
void main() {
  // gl_FragCoord is pixel-centre; framebuffer row 0 is written as crop row 0,
  // so readPixels (bottom-row-first) hands back top-row-first.
  vec3 dst = vec3(gl_FragCoord.x - 0.5, gl_FragCoord.y - 0.5, 1.0);
  vec3 src = uWinv * dst;
  if (abs(src.z) < 1e-9) { fragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
  vec2 p = src.xy / src.z;
  if (p.x < 0.0 || p.y < 0.0 || p.x > uSrcSize.x - 1.0 || p.y > uSrcSize.y - 1.0) {
    fragColor = vec4(0.0, 0.0, 0.0, 1.0); // BORDER_CONSTANT black, as in OpenCV
    return;
  }
  fragColor = vec4(texture(uSrc, (p + 0.5) / uSrcSize).rgb, 1.0);
}`

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const shader = gl.createShader(type)
  if (!shader) throw new Error('gaze warp: could not create shader')
  gl.shaderSource(shader, src)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader)
    gl.deleteShader(shader)
    throw new Error(`gaze warp: shader failed to compile — ${log}`)
  }
  return shader
}

/**
 * Reusable GPU warper. One instance per lab session: it holds a WebGL context
 * and browsers cap how many can exist, so one per frame runs fine for a minute
 * and then starts silently losing contexts.
 */
export class FaceWarper {
  private gl: WebGL2RenderingContext
  private program: WebGLProgram
  private sourceTexture: WebGLTexture
  private texture: WebGLTexture
  private framebuffer: WebGLFramebuffer
  private rgba = new Uint8Array(CROP_SIZE * CROP_SIZE * 4)
  private disposed = false

  constructor() {
    const canvas = document.createElement('canvas')
    canvas.width = CROP_SIZE
    canvas.height = CROP_SIZE
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    })
    if (!gl) throw new Error('gaze warp: WebGL2 is unavailable')
    this.gl = gl

    const program = gl.createProgram()
    if (!program) throw new Error('gaze warp: could not create program')
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX_SRC))
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, WARP_SRC))
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`gaze warp: program failed to link — ${gl.getProgramInfoLog(program)}`)
    }
    this.program = program

    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
    const loc = gl.getAttribLocation(program, 'aPos')
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

    const newTexture = (): WebGLTexture => {
      const texture = gl.createTexture()
      if (!texture) throw new Error('gaze warp: could not allocate texture')
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      return texture
    }
    this.sourceTexture = newTexture()
    this.texture = newTexture()
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, CROP_SIZE, CROP_SIZE, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    const framebuffer = gl.createFramebuffer()
    if (!framebuffer) throw new Error('gaze warp: could not allocate framebuffer')
    this.framebuffer = framebuffer
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.texture, 0)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  }

  /**
   * Warp one frame into `out` as uint8 planar RGB (NCHW for a batch of one),
   * the exact layout the ONNX graph takes: normalization.py flips BGR→RGB and
   * transposes to CHW before `session.run`. Returns false if the frame could
   * not be read.
   */
  warp(source: TexImageSource, sourceW: number, sourceH: number, W: Mat3, out: Uint8Array): boolean {
    if (this.disposed) return false
    const Winv = inverse3(W)
    if (!Winv) return false
    const gl = this.gl

    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source)
    } catch {
      return false // a video frame can be unavailable mid-seek
    }

    gl.useProgram(this.program)
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer)
    gl.viewport(0, 0, CROP_SIZE, CROP_SIZE)
    gl.uniform1i(gl.getUniformLocation(this.program, 'uSrc'), 0)
    gl.uniform2f(gl.getUniformLocation(this.program, 'uSrcSize'), sourceW, sourceH)
    // WebGL wants column-major; Mat3 here is row-major, hence the transpose.
    gl.uniformMatrix3fv(
      gl.getUniformLocation(this.program, 'uWinv'),
      false,
      new Float32Array([Winv[0], Winv[3], Winv[6], Winv[1], Winv[4], Winv[7], Winv[2], Winv[5], Winv[8]]),
    )
    gl.drawArrays(gl.TRIANGLES, 0, 3)
    gl.readPixels(0, 0, CROP_SIZE, CROP_SIZE, gl.RGBA, gl.UNSIGNED_BYTE, this.rgba)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)

    const plane = CROP_SIZE * CROP_SIZE
    for (let i = 0; i < plane; i++) {
      out[i] = this.rgba[i * 4]
      out[plane + i] = this.rgba[i * 4 + 1]
      out[2 * plane + i] = this.rgba[i * 4 + 2]
    }
    return true
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    const gl = this.gl
    gl.deleteTexture(this.sourceTexture)
    gl.deleteTexture(this.texture)
    gl.deleteFramebuffer(this.framebuffer)
    gl.deleteProgram(this.program)
    gl.getExtension('WEBGL_lose_context')?.loseContext()
  }
}
